import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import GigFormModal from '../components/GigFormModal';
import { API } from '../config/api';
import {
  Briefcase, Search, MapPin, DollarSign, Clock, User,
  Plus, Loader2, Filter, ArrowUpDown, Eye, MessageSquare,
  Headphones, Video, Palette, Building2, Mic2, Radio,
  Music, Camera, ChevronRight
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'artist', label: 'Artist', icon: Music },
  { value: 'producer', label: 'Producer', icon: Headphones },
  { value: 'engineer', label: 'Engineer', icon: Headphones },
  { value: 'studio', label: 'Studio', icon: Building2 },
  { value: 'videographer', label: 'Videographer', icon: Video },
  { value: 'graphic_designer', label: 'Graphic Designer', icon: Palette },
  { value: 'podcaster', label: 'Podcaster', icon: Mic2 },
  { value: 'streamer', label: 'Streamer', icon: Radio },
];

const ROLE_COLORS = {
  artist: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  producer: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  engineer: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  studio: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  videographer: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  graphic_designer: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  podcaster: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  streamer: 'bg-red-500/20 text-red-400 border-red-500/30',
};

function formatBudget(gig) {
  if (!gig.budget_min && !gig.budget_max) return null;
  if (gig.budget_min && gig.budget_max) return `$${gig.budget_min} - $${gig.budget_max}`;
  if (gig.budget_max) return `Up to $${gig.budget_max}`;
  if (gig.budget_min) return `From $${gig.budget_min}`;
  return null;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function HireBoardPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [gigs, setGigs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({ role: 'all', location: '', sort: 'newest', search: '' });
  const [searchInput, setSearchInput] = useState('');

  const fetchGigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.role && filters.role !== 'all') params.set('role', filters.role);
      if (filters.location) params.set('location', filters.location);
      if (filters.search) params.set('search', filters.search);
      params.set('sort', filters.sort);
      params.set('limit', '30');
      const res = await fetch(`${API}/gigs?${params}`);
      if (res.ok) {
        const d = await res.json();
        setGigs(d.gigs || []);
        setTotal(d.total || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchGigs(); }, [fetchGigs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(f => ({ ...f, search: searchInput }));
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="hire-board-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-secondary" />Hire Board
            </h1>
            <p className="text-muted-foreground mt-1">Post gigs, find talent, connect with creators</p>
          </div>
          <Button
            onClick={() => isAuthenticated ? setShowCreate(true) : navigate('/login')}
            className="bg-secondary hover:bg-secondary/80"
            data-testid="post-gig-btn"
          >
            <Plus className="w-4 h-4 mr-2" />Post a Gig
          </Button>
        </div>

        {/* Filters */}
        <Card className="glass-card border-border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search gigs..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="gig-search-input"
              />
            </form>
            <Select value={filters.role} onValueChange={v => setFilters(f => ({ ...f, role: v }))}>
              <SelectTrigger className="w-full sm:w-48" data-testid="gig-role-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.sort} onValueChange={v => setFilters(f => ({ ...f, sort: v }))}>
              <SelectTrigger className="w-full sm:w-40" data-testid="gig-sort-filter">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="trending">Trending</SelectItem>
                <SelectItem value="budget_high">Budget: High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-4">{total} {total === 1 ? 'gig' : 'gigs'} found</p>

        {/* Gig List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : gigs.length === 0 ? (
          <Card className="glass-card border-border p-16 text-center">
            <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">No gigs posted yet</h2>
            <p className="text-muted-foreground mb-4">Be the first to post a gig and connect with creators!</p>
            <Button onClick={() => isAuthenticated ? setShowCreate(true) : navigate('/login')} data-testid="post-first-gig-btn">
              <Plus className="w-4 h-4 mr-2" />Post a Gig
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="gig-list">
            {gigs.map(gig => {
              const budget = formatBudget(gig);
              const loc = gig.location_neighborhood || gig.location_city;
              return (
                <Card
                  key={gig.gig_id}
                  className="glass-card border-border p-5 hover:bg-muted/20 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/hire-board/${gig.gig_id}`)}
                  data-testid={`gig-card-${gig.gig_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      <h3 className="font-medium text-base group-hover:text-secondary transition-colors">{gig.title}</h3>

                      {/* Roles needed */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {gig.roles_needed?.map(r => (
                          <Badge key={r} className={`text-xs ${ROLE_COLORS[r] || 'bg-muted/40 text-muted-foreground'}`}>
                            {r.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>

                      {/* Meta row */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />@{gig.poster_handle}
                        </span>
                        {loc && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{loc}</span>}
                        {budget && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{budget}</span>}
                        {gig.timeline && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{gig.timeline}</span>}
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{gig.views_count || 0}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{gig.responses_count || 0}</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{timeAgo(gig.created_at)}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Gig Modal */}
      {showCreate && <GigFormModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchGigs(); }} />}
    </div>
  );
}
